import { RefObject } from 'react';
import IndexedDbWrapper from '../../../../../../core/wrappers/browserStorage/indexedDb-wrapper';
import {
    AI_GENERATION_TOUR_CARD_SCROLL_OFFSET,
    AI_GENERATION_TOUR_DB_TABLE,
} from './AIGenerationTour.constants';

export type AIGenerationTourStepData = {
    scrollOffset?: number;
};

export const setAIGenerationTourCompleted = async (): Promise<void> => {
    await IndexedDbWrapper.updateOrAddItemInDb(AI_GENERATION_TOUR_DB_TABLE, 1, { isActive: false });
};

export const hasAIGenerationTourRecord = async (): Promise<boolean> => {
    const item: unknown = await IndexedDbWrapper.getItemInDb(AI_GENERATION_TOUR_DB_TABLE, 1);
    return item !== null;
};

export const deleteAIGenerationTour = async (id: number): Promise<void> => {
    await IndexedDbWrapper.deleteItemFromDb(AI_GENERATION_TOUR_DB_TABLE, id);
};

const findScrollableParent = (element: HTMLElement): HTMLElement | null => {
    let parent = element.parentElement;

    while (parent) {
        const style = getComputedStyle(parent);
        const overflowY = style.overflowY;
        if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
            return parent;
        }
        parent = parent.parentElement;
    }

    return null;
};

export const scrollTourTargetIntoView = (targetSelector: string, offsetTop = AI_GENERATION_TOUR_CARD_SCROLL_OFFSET): void => {
    const target = document.querySelector(targetSelector);
    if (!target || !(target instanceof HTMLElement)) return;

    const scrollParent = findScrollableParent(target);
    const targetRect = target.getBoundingClientRect();
    const viewportBottom = window.innerHeight - 96;

    if (!scrollParent) {
        if (targetRect.top >= offsetTop && targetRect.bottom <= viewportBottom) {
            return;
        }

        const top = window.scrollY + targetRect.top - offsetTop;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        return;
    }

    const parentRect = scrollParent.getBoundingClientRect();
    const visibleTop = parentRect.top + offsetTop;
    const visibleBottom = parentRect.bottom - 48;

    if (targetRect.top >= visibleTop && targetRect.bottom <= visibleBottom) {
        return;
    }

    const nextScrollTop = scrollParent.scrollTop + (targetRect.top - parentRect.top) - offsetTop;

    scrollParent.scrollTo({
        top: Math.max(0, nextScrollTop),
        behavior: 'smooth',
    });
};

export const scrollTourToPageStart = (scrollContainerRef: RefObject<HTMLElement | null>): void => {
    window.scrollTo({ top: 0, behavior: 'smooth' });

    const startEl = scrollContainerRef.current?.parentElement;
    if (!startEl) return;

    let el: HTMLElement | null = startEl;
    while (el) {
        const style = getComputedStyle(el);
        if ((style.overflowY === 'auto' || style.overflowY === 'scroll') && el.scrollHeight > el.clientHeight) {
            el.scrollTo({ top: 0, behavior: 'smooth' });
            break;
        }
        el = el.parentElement;
    }
};

export const handleTourStepScroll = (
    stepIndex: number,
    targetSelector: string,
    scrollOffset: number,
    scrollContainerRef: RefObject<HTMLElement | null>
): void => {
    if (stepIndex === 0 || scrollOffset === 0) {
        scrollTourToPageStart(scrollContainerRef);
        return;
    }

    scrollTourTargetIntoView(targetSelector, scrollOffset);
};
